import { LiveBadge } from "@/components/myEntry/LiveBadge";
import type { WebUserRoundEntryUnit, WebUserRoundEntryUnitWithResults } from "@/types/WebUserRoundEntryUnit";
import { roundToFixed } from "@/utils";
import { pointsToYen } from "@/utils/pointsToYen";
import { CalendarIcon, GroupIcon, ThumbaCoin, ThumbaTrophyIcon } from "@roman/shared/components/Icons";
import { format } from "date-fns";
import { useMemo } from "react";
import { Link } from "react-router-dom";

export const RoundEntryCard = ({
  shouldShowDecimal = false,
  roundEntryUnit,
}: {
  roundEntryUnit: WebUserRoundEntryUnit | WebUserRoundEntryUnitWithResults;
  shouldShowDecimal: boolean;
}) => {
  const NON_APPLICABLE = "--";

  const linkPath = useMemo(() => {
    switch (roundEntryUnit.status) {
      case "schedule":
        return `/scheduled/${roundEntryUnit.id}?tab=users`;
      case "live":
        return `/results/${roundEntryUnit.id}`;
      case "cancelled":
        return "/";
      case "prizeDistributed":
        return `/results/${roundEntryUnit.id}`;
      default:
        return "/";
    }
  }, [roundEntryUnit.status, roundEntryUnit.id]);

  const roundName = roundEntryUnit.abbreviatedName;
  const startingDate = roundEntryUnit.sportEventStartTime;
  const entryFee = roundEntryUnit.entryFee;
  const prizePool = roundEntryUnit.prizePool;

  const prize = "prize" in roundEntryUnit ? roundEntryUnit.prize : null;
  const rank = "rank" in roundEntryUnit ? roundEntryUnit.rank : null;
  const fantasyPoints = "fantasyPoints" in roundEntryUnit ? roundEntryUnit.fantasyPoints : null;

  const currentPrizeClassName = () => {
    if (!prize) {
      return "";
    }
    return roundEntryUnit.status === "live" ? "text-[#01DCF9] text-[16px] blink" : "text-[#01DCF9]";
  };

  const goldColor = "#BD8E00";

  return (
    <div className="flex flex-col m-4 p-4 bg-white">
      <div className="font-semibold text-[14px] text-black py-2">{roundName}</div>

      <div className="flex items-center gap-5">
        <div className="flex items-center gap-1 text-gray">
          <ThumbaCoin className="w-[16px] h-[16px]" />
          {Number.parseInt(entryFee) !== 0 ? (
            <span className="font-bold text-[12px] text-dark-gray whitespace-nowrap">{entryFee}pt</span>
          ) : (
            <span className="text-base font-bold whitespace-nowrap">無料</span>
          )}
        </div>
        <div className="flex items-center gap-1 text-gray min-w-[100px]">
          <GroupIcon className="w-[16px] h-[16px] flex-shrink-0" />
          <span className="text-[10px] whitespace-nowrap">エントリー</span>
          <span className="font-bold text-[12px] text-dark-gray whitespace-nowrap">
            {roundEntryUnit.numEntries}/{roundEntryUnit.maxNumEntries}
          </span>
        </div>
        <div className="flex items-center gap-1 text-gray min-w-[120px]">
          <CalendarIcon className="w-[16px] h-[16px] flex-shrink-0" />
          <span className="text-[11px] whitespace-nowrap">開催日</span>
          <span className="font-bold text-[12px] text-dark-gray whitespace-nowrap">
            {format(new Date(startingDate), "yyyy.M.d")}
          </span>
        </div>
      </div>

      <div className="flex justify-between items-center py-4">
        <div className="flex items-center gap-2">
          <ThumbaTrophyIcon className="w-[25px] h-[25px]" />
          <span className="text-[12px] text-black whitespace-nowrap">賞金総額</span>
          <div className="flex items-end font-bold" style={{ color: goldColor }}>
            <span
              className={Number(prizePool) >= 100000000 ? "text-[21px] sm:text-[22px]" : "text-[22px] sm:text-[22px]"}
            >
              {pointsToYen(prizePool, true)}
            </span>
            <span
              className={Number(prizePool) >= 100000000 ? "text-[13.5px] sm:text-[14px]" : "text-[14px] sm:text-[14px]"}
            >
              円
            </span>
          </div>
        </div>

        <Link to={linkPath} state={{ roundStatus: roundEntryUnit.status }}>
          {roundEntryUnit.status === "live" ? (
            <LiveBadge
              isInTab={false}
              isSelectedTab={false}
              isInMenu={false}
              text="LIVE"
              className="items-center justify-center"
              iconClassName=""
              textClassName="font-semibold text-[12px]"
            />
          ) : (
            <button
              type="button"
              className="px-3.5 sm:px-4 py-1 my-1 flex items-center space-x-1 rounded-full border-[1px] bg-white border-primary text-[9px] sm:text-[10px] text-primary"
            >
              <span className="whitespace-nowrap">詳細を見る</span>
              <span className="font-bold">&gt;</span>
            </button>
          )}
        </Link>
      </div>

      <div className="text-center text-white">
        <div className="flex bg-primary gap-2 text-[12px]">
          <div className="w-1/3 py-2">獲得中の賞金</div>
          <div className="w-1/3 py-2 border-x border-white">順位</div>
          <div className="w-1/3 py-2">選手合計pt</div>
        </div>
        <div className="flex bg-[#004BC3] gap-2 font-bold text-[14px]">
          <div className="w-1/3 py-2">
            <div className={currentPrizeClassName()}>
              {prize ? (
                <div className="flex justify-center items-center text-[16px] font-bold">
                  <div>{pointsToYen(prize, shouldShowDecimal)}</div>
                  <div>円</div>
                </div>
              ) : (
                NON_APPLICABLE
              )}
            </div>
          </div>
          <div className="w-1/3 py-2 border-x border-white">
            {rank && roundEntryUnit.numEntries ? `${rank}/${roundEntryUnit.numEntries}` : NON_APPLICABLE}
          </div>
          <div className="w-1/3 py-2">{fantasyPoints ? roundToFixed(fantasyPoints) : NON_APPLICABLE}</div>
        </div>
      </div>
    </div>
  );
};
